import React, { useState } from 'react';
import { Plus, Download, Send, CreditCard, AlertCircle } from 'lucide-react';
import { Button, Badge, Input, Card, DataTable } from '@clinic-os/ui';
import type { Column } from '@clinic-os/ui';
import type { Invoice } from '@clinic-os/shared/types';
import { useInvoices, useMarkInvoicePaid } from '../../hooks/useInvoices';
import { useCreateCheckoutSession, useCreatePaymentLink, useStripeConfigured } from '../../hooks/useStripe';
import { useSendInvoice, useMessagingConfigured } from '../../hooks/useMessaging';
import { DEMO_PATIENTS } from '../../data/demo-data';

const statusVariant: Record<string, 'success' | 'warning' | 'error' | 'default' | 'outline'> = {
  paid: 'success',
  sent: 'default',
  partial: 'warning',
  overdue: 'error',
  draft: 'outline',
  cancelled: 'error',
};

function getPatientName(patientId: string): string {
  const patient = DEMO_PATIENTS.find((p) => p.id === patientId);
  return patient ? `${patient.firstName} ${patient.lastName}` : `Patient #${patientId}`;
}

export function InvoicesPage() {
  const [search, setSearch] = useState('');
  const [selectedChannel, setSelectedChannel] = useState<'sms' | 'whatsapp'>('whatsapp');
  const { data: invoices = [], isLoading } = useInvoices();
  const markPaid = useMarkInvoicePaid();
  const createCheckout = useCreateCheckoutSession();
  const createPaymentLink = useCreatePaymentLink();
  const sendInvoice = useSendInvoice();
  const isStripeConfigured = useStripeConfigured();
  const isMessagingConfigured = useMessagingConfigured();

  // Default clinic slug - in production this would come from clinic settings
  const clinicSlug = 'demo';

  const filtered = invoices.filter(
    (i) =>
      i.invoiceNumber.toLowerCase().includes(search.toLowerCase()) ||
      i.status.toLowerCase().includes(search.toLowerCase())
  );

  const totalOutstanding = invoices
    .filter((i) => i.status === 'sent' || i.status === 'overdue')
    .reduce((sum, i) => sum + i.total, 0);

  const columns: Column<Invoice>[] = [
    {
      key: 'invoiceNumber',
      header: 'Invoice #',
      render: (i) => <span className="font-mono text-sm font-medium">{i.invoiceNumber}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      render: (i) => <span className="text-slate-700">{getPatientName(i.patientId)}</span>,
    },
    {
      key: 'items',
      header: 'Items',
      render: (i) => <span className="text-slate-500 text-sm">{i.items.length} item{i.items.length !== 1 ? 's' : ''}</span>,
    },
    {
      key: 'total',
      header: 'Amount',
      render: (i) => <span className="font-medium">${i.total.toFixed(2)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (i) => <Badge variant={statusVariant[i.status]}>{i.status}</Badge>,
    },
    {
      key: 'dueDate',
      header: 'Due Date',
      render: (i) => (
        <span className={`text-sm ${i.status === 'overdue' ? 'text-red-700 font-medium' : 'text-slate-500'}`}>
          {i.dueDate ? new Date(i.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      header: '',
      render: (i) => (
        <div className="flex items-center gap-2">
          {(i.status === 'sent' || i.status === 'overdue') && (
            <>
              {isMessagingConfigured ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    // First create payment link, then send via messaging
                    createPaymentLink.mutate(
                      { invoice: i, clinicSlug },
                      {
                        onSuccess: (data) => {
                          sendInvoice.mutate({
                            invoiceId: i.invoiceNumber,
                            patientId: i.patientId,
                            paymentLink: data.url,
                            channel: selectedChannel,
                          });
                        },
                      }
                    );
                  }}
                  disabled={createPaymentLink.isPending || sendInvoice.isPending}
                >
                  <Send className="w-4 h-4" />
                  Send
                </Button>
              ) : (
                <div className="group relative">
                  <Button variant="ghost" size="sm" disabled>
                    <Send className="w-4 h-4" />
                    Send
                  </Button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                    Not configured
                  </div>
                </div>
              )}
              {isStripeConfigured ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    createCheckout.mutate({ invoice: i, clinicSlug });
                  }}
                  disabled={createCheckout.isPending}
                >
                  <CreditCard className="w-4 h-4" />
                  Pay Now
                </Button>
              ) : (
                <div className="group relative">
                  <Button variant="outline" size="sm" disabled>
                    <CreditCard className="w-4 h-4" />
                    Pay Now
                  </Button>
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-slate-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none z-10">
                    Not configured
                  </div>
                </div>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  markPaid.mutate(i.id);
                }}
              >
                Mark Paid
              </Button>
            </>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Invoices</h1>
          <p className="text-sm text-slate-500 mt-1">
            ${totalOutstanding.toFixed(2)} outstanding
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {!isStripeConfigured && !isMessagingConfigured && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Set up Stripe and Twilio in .env to enable payments and messaging</span>
            </div>
          )}
          <Button variant="outline">
            <Download className="w-4 h-4" />
            Export
          </Button>
          <Button>
            <Plus className="w-4 h-4" />
            New Invoice
          </Button>
        </div>
      </div>

      {/* Channel selector for messaging */}
      <div className="flex items-center gap-4 mb-4">
        <span className="text-sm text-slate-500">Send via:</span>
        <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setSelectedChannel('sms')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              selectedChannel === 'sms'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            SMS
          </button>
          <button
            type="button"
            onClick={() => setSelectedChannel('whatsapp')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              selectedChannel === 'whatsapp'
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            WhatsApp
          </button>
        </div>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search invoices..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-sm"
        />
      </div>

      <DataTable
        label="Invoices"
        columns={columns}
        data={filtered}
        keyExtractor={(i) => i.id}
        emptyMessage={isLoading ? 'Loading...' : 'No invoices found'}
      />
    </div>
  );
}
